import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Button } from "react-md";
import MGridForm from "components/Shared/MGridForm";
import {
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_STRING,
  DATA_TYPE_DATE,
  DATA_TYPE_CHECKBOX,
} from "typeConstants/dataTypes";
import MDialog from "components/Shared/MDialog.tsx";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { MSpinner } from "components/Shared";

const employmentTypes = [{ label: "Full-time", value: "true" }, { label: "Part-time", value: "false" }];

const FieldRepCreate = props => {
  const formControlMethods = useRef({});

  const stateOptions = props.states && props.states.map(x => ({ label: x.stateAbbreviation, value: x.stateAbbreviation }));
  const teamLeaderOptions = props.teamLeaders && props.teamLeaders.map(x => ({ label: x.firstLastName, value: x.inspectorNumber }));
  const fieldLevelCoordinatorOptions = props.fieldLevelCoordinators && props.fieldLevelCoordinators.map(x => ({ label: `${x.firstName} ${x.lastName}`, value: `${x.internetUserId}` }));
  const fieldDevelopmentManagerOptions = props.fieldDevelopmentManagers && props.fieldDevelopmentManagers.map(x => ({ label: `${x.firstName} ${x.lastName}`, value: `${x.employeeId}` }));

  const mGridFormDefinition = [
    [
      { dataType: DATA_TYPE_STRING, label: "Mueller Email", source: "userEmail", hasValidationError: validator.combineValidators(validator.required, validator.muellerEmailAddress) }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Personal Email", source: "personalEmail", hasValidationError: validator.combineValidators(validator.required, validator.emailAddress) }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "First Name", source: "firstName", hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Last Name", source: "lastName", hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Street Address", source: "address", hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "City", source: "city", hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "State: ", source: "state", dataTypeOptions: { options: stateOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Zip", source: "postalCode", hasValidationError: validator.zipcode }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Phone", source: "phoneNumber", hasValidationError: validator.phoneNumber }
    ],
    [
      { key: "isFake", dataType: DATA_TYPE_CHECKBOX, label: "Is Fake", source: "isFake", resizable: false, }
    ],
    [
      { dataType: DATA_TYPE_DATE, label: "Hire Date", source: "hireDate", hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Employment Type: ", source: "isFullTime", dataTypeOptions: { options: employmentTypes }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Team Leader: ", source: "teamLeaderInternetUserId", dataTypeOptions: { options: teamLeaderOptions } }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Manager: ", source: "managerInternetUserId", dataTypeOptions: { options: fieldLevelCoordinatorOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Field Development Manager: ", source: "fieldDevelopmentEmployeeId", dataTypeOptions: { options: fieldDevelopmentManagerOptions } }
    ],
  ];

  function submit(callback) {
    if (formControlMethods.current.isFormValid()) {
      callback();
    }
  }

  const getDialogActions = () => {
    let actions = [];
    actions.push(<Button primary raised disabled={props.creatingFieldRep} onClick={() => submit(props.onSubmitCreateAnother)} >Save and Make Another</Button>);
    actions.push(<Button primary raised disabled={props.creatingFieldRep} onClick={() => submit(props.onSubmit)} >Save</Button>);
    actions.push({ secondary: false, disabled: props.creatingFieldRep, children: "Cancel", onClick: props.hideCreateDialog });
    return actions;
  };

  return (
    <MDialog
      id="fr-create-dialog"
      className="fr-create-dialog"
      title="Create New Field Rep"
      contentActions={getDialogActions()}
      focusOnMount={false}
      width={525}
    >
      {props.creatingFieldRep ? <MSpinner /> :
        <MGridForm
          width={500}
          data={props.newFieldRep}
          onChange={props.handleFormChange}
          formDefinition={MGridForm.parseSections([{ rows: mGridFormDefinition }])}
          portalTarget={() => document.querySelector(".fr-create-dialog")}
          getControlMethods={cm => { formControlMethods.current = cm; }}
          webform
        />}
    </MDialog>
  );
};

FieldRepCreate.propTypes = {
  newFieldRep: PropTypes.object.isRequired,           // FR to create, an object that is stored in state of parent
  handleFormChange: PropTypes.func.isRequired,        // Function that MGridForm will pass an object that represents the data passed in only with changed values, the source of the last field changed, the dataType of the last field changed, the value of the last field changed value, an array of all of the sources changed so far
  teamLeaders: PropTypes.array.isRequired,            // An array of all Team Leaders
  fieldLevelCoordinators: PropTypes.array.isRequired, // An array of all FLCs
  fieldDevelopmentManagers: PropTypes.array.isRequired, // an array of all FDM
  states: PropTypes.array.isRequired,                 // An array of state abbreviations
  hideCreateDialog: PropTypes.func.isRequired,        // This should set createDialogVisible to false
  onSubmit: PropTypes.func.isRequired,                // Called when user clicks Save button
  onSubmitCreateAnother: PropTypes.func.isRequired,   // Called when user clicks Save and Make Another button
  creatingFieldRep: PropTypes.bool.isRequired,        // Track if we are creating a field rep
};

export default FieldRepCreate;
